import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Camera, User, BadgeCheck, Hand, Check, Save, X, Calendar, ChevronRight, ShieldCheck, Phone, Layout, Info, Settings, Trophy, Upload, Target, MapPin, Edit } from 'lucide-react';
import { Area } from 'react-easy-crop'; // If not used, we can use any, but keep for type compatibility with image-utils
import { CustomImageCropper } from '../common/CustomImageCropper';
import { getCroppedImg, resizeImageFile } from '../../lib/image-utils';
import { db, storage } from '../../lib/firebase';
import { doc, writeBatch, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { UserProfile, Player } from '../../types';
import { motion, AnimatePresence } from 'motion/react';
import { PROVINCES, CITIES_BY_PROVINCE } from '../../data/locations';
import { FadeInImage } from '../common/FadeInImage';
import { WhatsAppVerificationModal } from '../common/WhatsAppVerificationModal';

interface EditProfileModalProps {
  user: UserProfile;
  player: Player;
  onComplete: (profile: UserProfile) => void;
  onCancel: () => void;
  logoUrl?: string;
  clubs?: any[];
}

export function EditProfileModal({ user, player, onComplete, onCancel, logoUrl, clubs = [] }: EditProfileModalProps) {
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [targetClubId, setTargetClubId] = useState('');
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Lock scroll and recover viewport coordinates on close to avoid mobile zoom shifting glitches
  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      const vv = window.visualViewport;
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        zIndex: 200,
      };
    }
    return {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 200,
    };
  });

  const [cardStyle, setCardStyle] = useState<React.CSSProperties>(() => {
    if (typeof window !== 'undefined' && window.visualViewport) {
      const scale = window.visualViewport.scale || 1;
      return {
        transform: `scale(${1 / scale})`,
        transformOrigin: 'center center',
      };
    }
    return {};
  });

  useEffect(() => {
    // Record current scroll position to freeze background scroll beautifully
    const scrollY = window.scrollY;

    // Save original styles of document.body
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalTop = document.body.style.top;
    const originalLeft = document.body.style.left;
    const originalWidth = document.body.style.width;

    // Freeze background scroll completely (necessary for iOS Safari)
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.width = '100%';

    // Track visual viewport to keep the container perfectly aligned and the card perfectly inverse-scaled
    const updateViewport = () => {
      if (window.visualViewport) {
        const vv = window.visualViewport;
        setViewportStyle({
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100dvh',
          zIndex: 200,
        });
        const scale = vv.scale || 1;
        setCardStyle({
          transform: `scale(${1 / scale})`,
          transformOrigin: 'center center',
        });
      }
    };

    updateViewport();

    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);

    // Stop native touch event propagation to prevent triggering the global index.html virtual zoom
    const handleTouchStop = (e: TouchEvent) => {
      e.stopPropagation();
    };

    const el = modalRef.current;
    if (el) {
      el.addEventListener('touchstart', handleTouchStop, { passive: true });
      el.addEventListener('touchmove', handleTouchStop, { passive: true });
      el.addEventListener('touchend', handleTouchStop, { passive: true });
      el.addEventListener('touchcancel', handleTouchStop, { passive: true });
    }

    return () => {
      // Restore original body styles
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.top = originalTop;
      document.body.style.left = originalLeft;
      document.body.style.width = originalWidth;

      // Restore physical scroll position
      window.scrollTo(0, scrollY);

      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      if (el) {
        el.removeEventListener('touchstart', handleTouchStop);
        el.removeEventListener('touchmove', handleTouchStop);
        el.removeEventListener('touchend', handleTouchStop);
        el.removeEventListener('touchcancel', handleTouchStop);
      }
    };
  }, [mounted]);

  const [name, setName] = useState(player.name.split(' ')[0] || '');
  const [surname, setSurname] = useState(player.name.split(' ').slice(1).join(' ') || '');
  const [nickname, setNickname] = useState(player.nickname || '');
  const [phone, setPhone] = useState(player.phone || '');
  const [isPhoneVerified, setIsPhoneVerified] = useState(player.phoneVerified || false);
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [province, setProvince] = useState(() => {
    let loc = user.location || 'RN'; // Default to RN if empty since it's the only one for now
    if (loc === 'Río Negro' || loc === 'Rio Negro') return 'RN';
    // Attempt to match if the province name is stored instead of ID
    const matched = PROVINCES.find(p => p.name === loc || p.id === loc);
    return matched ? matched.id : loc;
  });
  const [city, setCity] = useState(() => {
    let c = user.city || '';
    // Try to find if it's stored by old name
    for (const provId in CITIES_BY_PROVINCE) {
      const matched = CITIES_BY_PROVINCE[provId].find(cityObj => cityObj.name === c || cityObj.id === c || cityObj.name.includes(c));
      if (matched) return matched.id;
    }
    return c;
  });
  const [position, setPosition] = useState<'drive' | 'reves' | 'ambas'>(player.position || 'ambas');

  // Category Request States
  const [requestingCategory, setRequestingCategory] = useState(false);
  const [requestedCat, setRequestedCat] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  // Image handles
  const [image, setImage] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [finalImage, setFinalImage] = useState<string | null>(player.photoUrl || null);
  const [isCropping, setIsCropping] = useState(false);

  const onCropComplete = useCallback((croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setFieldErrors(prev => ({ ...prev, photo: '¡DEMASIADO PESADA! (MÁX 10MB)' }));
        setError('La imagen supera el límite de 10MB.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        const resizedBase64 = await resizeImageFile(file, 1200);
        setImage(resizedBase64);
        setIsCropping(true);
        if (fieldErrors.photo) setFieldErrors(prev => ({ ...prev, photo: '' }));
      } catch (err) {
        console.error(err);
        setError('Error al procesar la imagen');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleApplyCrop = async () => {
    if (!image || !croppedAreaPixels) return;
    try {
      setLoading(true);
      const cropped = await getCroppedImg(image, croppedAreaPixels, 0, { horizontal: false, vertical: false }, 400);
      
      const imageRef = ref(storage, `avatars/${player.id}_${Date.now()}.jpg`);
      await uploadString(imageRef, cropped, 'data_url');
      const photoUrl = await getDownloadURL(imageRef);
      
      setFinalImage(photoUrl);
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      setError('Error al recortar la imagen');
    } finally {
      setLoading(false);
    }
  };

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    if (!mounted || !name || !finalImage) return;

    const timeout = setTimeout(async () => {
      setSaveStatus('saving');
      setError('');

      try {
        const fullName = `${name} ${surname}`.trim();
        const batch = writeBatch(db);

        const updatedProfile: UserProfile = {
          ...user,
          name: fullName,
          photoUrl: finalImage,
          phone,
          phoneVerified: isPhoneVerified,
          location: province,
          city,
        };

        batch.set(doc(db, 'users', user.uid), {
          name: fullName,
          photoUrl: finalImage,
          phone,
          phoneVerified: isPhoneVerified,
          location: province,
          city,
        }, { merge: true });

        batch.set(doc(db, 'players', user.uid), {
          name: fullName,
          nickname,
          position,
          photoUrl: finalImage,
          phone,
          phoneVerified: isPhoneVerified,
          city,
        }, { merge: true });

        // Trigger validation request if they add or change their photo and aren't validated
        if (!player.isValidated && finalImage && finalImage !== player.photoUrl) {
          batch.update(doc(db, 'players', user.uid), {
            validationReviewed: false,
            validationRejected: false
          });

          const adminsQuery = query(collection(db, 'users'), where('role', '==', 'superadmin'));
          const adminsSnap = await getDocs(adminsQuery);
          
          const userNotifRef = doc(collection(db, 'notifications'));
          batch.set(userNotifRef, {
            userId: user.uid,
            title: '¡Perfil en validación!',
            body: 'Has subido tu foto de perfil. Un administrador evaluará tu perfil para validarlo a la brevedad.',
            type: 'player_validation',
            createdAt: Date.now(),
            read: false
          });

          adminsSnap.forEach(adminDoc => {
            const adminNotifRef = doc(collection(db, 'notifications'));
            batch.set(adminNotifRef, {
              userId: adminDoc.id,
              title: 'Validación de Jugador Pendiente',
              body: `El jugador ${fullName} ha subido su foto y requiere validación (Cat: ${player.category}).`,
              type: 'admin_validation',
              playerId: user.uid,
              createdAt: Date.now(),
              read: false
            });
          });
        }

        await batch.commit();
        setSaveStatus('saved');
        
        // We quietly update the parent component's profile state if possible, or just let Firestore snapshot handle it
        // However calling onComplete(updatedProfile) might close the modal if the parent does setShowEditModal(false) inside it
        // So we won't call onComplete here. We only call onComplete when closing.
      } catch (e: any) {
        console.error(e);
        handleFirestoreError(e, 'write', 'users/players');
        setSaveStatus('idle');
      }
    }, 1500);

    return () => {
      clearTimeout(timeout);
      if (saveStatus === 'saved') setSaveStatus('idle');
    };
  }, [name, surname, nickname, phone, isPhoneVerified, province, city, position, finalImage]);

  const handleClose = () => {
    // Pass back current known data to parent so it can update its local state before closing
    onComplete({
      ...user,
      name: `${name} ${surname}`.trim(),
      photoUrl: finalImage || user.photoUrl,
      phone,
      phoneVerified: isPhoneVerified,
      location: province,
      city,
    });
  };

  const handleRequestCategory = async () => {
    if (!requestedCat || !acceptedTerms || !targetClubId) return;
    
    setRequestLoading(true);
    try {
      const batch = writeBatch(db);
      const requestRef = doc(collection(db, 'categoryRequests'));
      const now = Date.now();
      const targetClub = clubs.find((c: any) => (c.uid || c.id) === targetClubId);

      batch.set(requestRef, {
        playerId: player.id,
        playerName: player.name,
        currentCategory: player.category,
        requestedCategory: requestedCat,
        targetClubId: targetClubId,
        targetClubName: targetClub?.name || '',
        status: 'pending',
        createdAt: now
      });

      // Notify the club owner/admin
      const adminNotifRef = doc(collection(db, 'notifications'));
      batch.set(adminNotifRef, {
        userId: targetClubId,
        title: 'Solicitud de Cambio de Categoría',
        body: `${player.name} solicita pasar de ${player.category} a ${requestedCat}.`,
        type: 'admin_category_request',
        playerId: player.id,
        createdAt: now,
        read: false
      });

      // Notify staff members of the club
      const staffQuery = query(collection(db, 'users'), where('clubId', '==', targetClubId));
      const staffSnap = await getDocs(staffQuery);
      
      staffSnap.forEach(staffDoc => {
        const data = staffDoc.data();
        if (['co_admin', 'gestor_torneos'].includes(data.role)) {
          const notifRef = doc(collection(db, 'notifications'));
          batch.set(notifRef, {
            userId: staffDoc.id,
            title: 'Solicitud de Cambio de Categoría',
            body: `${player.name} solicita pasar de ${player.category} a ${requestedCat}.`,
            type: 'admin_category_request',
            playerId: player.id,
            createdAt: now,
            read: false
          });
        }
      });

      await batch.commit();
      setRequestingCategory(false);
      setRequestedCat('');
      setAcceptedTerms(false);
      alert('Solicitud enviada con éxito. Un administrador la revisará a la brevedad.');
    } catch (e) {
      console.error(e);
      setError('Error al enviar la solicitud');
    } finally {
      setRequestLoading(false);
    }
  };

  const categories = ['8va', '7ma', '6ta', '5ta', '4ta', '3ra', '2da', '1ra'];

  if (!mounted) return null;

  return createPortal(
    <div ref={modalRef} className="fixed inset-0 z-[200] flex items-center justify-center px-2 py-4 sm:px-4 sm:py-6 bg-black/40">
      <style>{`
        @keyframes metallic-sweep {
          0% { left: -150%; }
          40% { left: 150%; }
          100% { left: 150%; }
        }
        .btn-sweep-1, .btn-sweep-2 {
          position: relative;
          overflow: hidden;
        }
        .btn-sweep-1::after {
          content: '';
          position: absolute;
          top: 0;
          left: -150%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.35) 50%,
            transparent
          );
          transform: skewX(-25deg);
          animation: metallic-sweep 3s infinite ease-in-out;
        }
        .btn-sweep-2::after {
          content: '';
          position: absolute;
          top: 0;
          left: -150%;
          width: 60%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.55) 50%,
            transparent
          );
          transform: skewX(-25deg);
          animation: metallic-sweep 3s infinite ease-in-out;
          animation-delay: 0.8s;
        }
      `}</style>
      <div style={cardStyle} className="w-[96%] max-w-[96%] md:max-w-4xl mx-auto flex flex-col max-h-[88dvh] sm:max-h-[86vh] pointer-events-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative w-full h-full bg-slate-800/[0.85] backdrop-blur-sm transform-gpu border border-white/10 rounded-[1.5rem] md:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
        >
        {/* Ambient Radial Lights inside the Modal for Premium Glow */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-gradient-to-br from-lime-500/10 via-emerald-500/5 to-transparent blur-3xl pointer-events-none -z-10" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tr from-cyan-500/10 via-blue-500/5 to-transparent blur-3xl pointer-events-none -z-10" />

        {/* Header */}
        <div className="p-4 md:p-8 pb-3 md:pb-4 flex flex-col items-center justify-center border-b border-white/5 relative shrink-0">
          <div className="flex flex-col items-center text-center gap-1.5 md:gap-2">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl md:rounded-2xl theme-primary-bg/10 flex items-center justify-center">
              <Edit className="theme-primary-text" size={20} />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-black uppercase italic theme-primary-text tracking-tighter leading-none">
                Editar Perfil
              </h2>
              <p className="text-[9px] md:text-[10px] font-bold opacity-30 uppercase tracking-widest mt-1 md:mt-1.5">Actualiza tus datos</p>
            </div>
          </div>
          <div className="absolute right-4 md:right-16 top-1/2 -translate-y-1/2 flex items-center">
            {saveStatus === 'saving' && <span className="text-[9px] md:text-[10px] font-bold text-white/50 uppercase tracking-widest animate-pulse flex items-center gap-1"><Settings size={10} className="animate-spin" /> <span className="hidden sm:inline">Guardando</span></span>}
            {saveStatus === 'saved' && <span className="text-[9px] md:text-[10px] font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-1"><Check size={10} /> <span className="hidden sm:inline">Guardado</span></span>}
          </div>
        </div>

        <div className={`flex-1 px-4 md:px-8 py-4 md:py-6 custom-scrollbar ${isCropping ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <AnimatePresence mode="wait">
            {isCropping ? (
              <motion.div
                key="cropper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6"
              >
                  <CustomImageCropper
                    image={image!}
                    onCropComplete={onCropComplete}
                    containerSize={280}
                  />
                  <div className="flex flex-col-reverse sm:flex-row gap-3 w-full max-w-[300px] mx-auto mt-4">
                    <button
                      onClick={() => setIsCropping(false)}
                      className="flex-1 w-full bg-white/5 py-4 rounded-2xl font-black uppercase text-xs hover:bg-white/10 transition-all border border-white/5 text-white"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleApplyCrop}
                      className="flex-1 w-full theme-primary-bg text-black py-4 rounded-2xl font-black uppercase text-xs shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                    >
                      <span>Aplicar Recorte</span>
                    </button>
                  </div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                {/* Column 1: Identity & Photo */}
                <div className="space-y-4 md:space-y-6">
                  <div className="flex flex-col items-center mb-2">
                    <div className="relative">
                      <div className="w-24 h-24 md:w-32 md:h-32 rounded-[1.5rem] md:rounded-[2rem] overflow-hidden border-2 border-white/90 shadow-[0_0_25px_rgba(255,255,255,0.15)] bg-black/40 transition-all">
                        {finalImage ? (
                          <FadeInImage src={finalImage} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center opacity-20">
                            <User size={48} />
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-2 -right-4 flex gap-1.5 z-20">
                        <label className="w-9 h-9 bg-slate-800 border border-white/15 text-white rounded-full flex items-center justify-center cursor-pointer shadow-xl hover:scale-110 active:scale-95 transition-all btn-sweep-1" title="Subir desde galería">
                          <Upload size={16} strokeWidth={2.5} />
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                        </label>
                        <label className="w-9 h-9 theme-primary-bg text-black rounded-full flex items-center justify-center cursor-pointer shadow-xl hover:scale-110 active:scale-95 transition-all btn-sweep-2" title="Tomar selfie">
                          <Camera size={16} strokeWidth={2.5} />
                          <input type="file" className="hidden" accept="image/*" capture="user" onChange={handleImageUpload} />
                        </label>
                      </div>
                      
                      {fieldErrors.photo && (
                        <div className="absolute top-0 left-0 right-0 bg-red-500/90 backdrop-blur-sm py-2 text-center rounded-t-[1.8rem] z-[30] animate-in slide-in-from-top-full">
                          <span className="text-[8px] font-black text-white uppercase">{fieldErrors.photo}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Nombre</label>
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl px-3 md:px-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-xs text-white placeholder-white/20 transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Apellido</label>
                      <input
                        type="text"
                        value={surname}
                        onChange={e => setSurname(e.target.value)}
                        className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl px-3 md:px-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-xs text-white placeholder-white/20 transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 md:gap-3">
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Apodo (Opcional)</label>
                      <input
                        type="text"
                        value={nickname}
                        onChange={e => setNickname(e.target.value)}
                        placeholder="Ej: El Mago"
                        className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl px-3 md:px-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-xs text-white placeholder-white/20 transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md"
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between items-center ml-1">
                        <label className="text-[9px] font-black uppercase tracking-wider text-slate-200">Teléfono / WhatsApp</label>
                        {phone.length >= 10 && (
                          <button
                            type="button"
                            onClick={() => !isPhoneVerified && setShowPhonePrompt(true)}
                            disabled={isPhoneVerified}
                            className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full flex items-center gap-1 transition-all ${
                              isPhoneVerified 
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                                : 'bg-theme-primary/20 theme-primary-text border border-theme-primary/30 hover:bg-theme-primary/30'
                            }`}
                          >
                            {isPhoneVerified ? (
                              <><Check size={10} /> Verificado</>
                            ) : (
                              'Verificar'
                            )}
                          </button>
                        )}
                      </div>
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          setPhone(val);
                          if (val !== player.phone) {
                            setIsPhoneVerified(false);
                          } else {
                            setIsPhoneVerified(player.phoneVerified || false);
                          }
                        }}
                        placeholder="Ej: 2984123456"
                        className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl px-3 md:px-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-xs text-white placeholder-white/20 transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md"
                      />
                      <p className="text-[8px] text-white/40 ml-1 italic font-bold">Verifica para reservas de turnos y torneos</p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Provincia</label>
                      <div className="relative">
                        <select
                          value={province}
                          onChange={(e) => {
                            setProvince(e.target.value);
                            setCity(''); // Reset city when province changes
                          }}
                          className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl pl-8 md:pl-10 pr-2 md:pr-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-[10px] md:text-xs text-white transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md appearance-none"
                        >
                          <option value="" disabled className="bg-slate-900 text-white/50">Seleccionar provincia</option>
                          {PROVINCES.map(p => (
                            <option key={p.id} value={p.id} className="bg-slate-900 text-white">{p.name}</option>
                          ))}
                        </select>
                        <MapPin size={12} className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 opacity-40 theme-primary-text pointer-events-none md:w-[14px] md:h-[14px]" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200 ml-1">Ciudad</label>
                      <div className="relative">
                        <select
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          disabled={!province}
                          className="w-full bg-slate-950/40 border border-white/10 rounded-xl md:rounded-2xl pl-8 md:pl-10 pr-2 md:pr-4 py-2.5 md:py-3.5 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-[10px] md:text-xs text-white transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] backdrop-blur-md appearance-none disabled:opacity-50"
                        >
                          <option value="" disabled className="bg-slate-900 text-white/50">Seleccionar ciudad</option>
                          {province && CITIES_BY_PROVINCE[province]?.map(c => (
                            <option key={c.id} value={c.id} className="bg-slate-900 text-white">{c.name}</option>
                          ))}
                        </select>
                        <MapPin size={12} className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 opacity-40 theme-primary-text pointer-events-none md:w-[14px] md:h-[14px]" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Column 2: Game Details & Category Request */}
                <div className="space-y-4 md:space-y-6">
                  {/* Court Position */}
                  <div className="space-y-2 md:space-y-2 bg-slate-950/30 p-2.5 md:p-4 rounded-2xl md:rounded-3xl border border-white/10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.2)] backdrop-blur-md">
                    <div className="flex items-center gap-1.5 justify-center mb-1">
                      <Target size={12} className="theme-primary-text" />
                      <label className="text-[9px] font-black uppercase tracking-wider text-slate-200">Posición en cancha</label>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {(['drive', 'reves', 'ambas'] as const).map(p => (
                        <button
                          key={p}
                          onClick={() => setPosition(p)}
                          className={`py-2.5 rounded-xl text-[10px] font-black uppercase transition-all duration-300 flex items-center justify-center gap-2 border backdrop-blur-md ${
                            position === p 
                              ? 'theme-primary-bg text-black shadow-[0_8px_20px_rgba(var(--glow-rgb),0.35)] scale-[1.02] border-theme-primary' 
                              : 'bg-white/10 border-white/15 text-slate-100 hover:bg-white/20 hover:text-white hover:border-white/30 shadow-md'
                          }`}
                        >
                          {p === 'ambas' ? 'Ambos' : p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category Status & Request */}
                  <div className="space-y-2.5 md:space-y-3 bg-slate-950/30 p-3 md:p-5 rounded-2xl md:rounded-3xl border border-white/10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.2)] backdrop-blur-md">
                    <div className="flex items-center gap-1.5 justify-center">
                      <Trophy size={14} className="theme-primary-text" />
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-200">Categoría & Validación</label>
                    </div>

                    <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 p-2 md:p-3 rounded-xl md:rounded-2xl">
                      <Info size={14} className="text-yellow-500 mt-0.5 shrink-0" />
                      <p className="text-[8px] md:text-[9px] text-yellow-500/90 font-black uppercase tracking-wider leading-relaxed">
                        Todos los cambios de categoría están sujetos a la validación de un administrador.
                      </p>
                    </div>

                    <div className="flex items-center justify-between bg-slate-950/50 p-3 md:p-4 rounded-xl md:rounded-2xl border border-white/5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.2)]">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-black uppercase opacity-50 text-slate-300">Categoría Actual</span>
                        <span className="text-xl font-black theme-primary-text italic tracking-tighter mt-0.5">{player.category}</span>
                      </div>
                      <div className="flex items-center gap-1.5 bg-theme-primary/10 border border-theme-primary/20 px-3 py-1.5 rounded-xl">
                        <BadgeCheck size={14} className="theme-primary-text" />
                        <span className="text-[8px] font-black uppercase theme-primary-text">Validada</span>
                      </div>
                    </div>

                    {/* Request Category Change options */}
                    <button
                      onClick={() => setRequestingCategory(true)}
                      className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white py-3 rounded-xl font-black uppercase text-[10px] tracking-wide transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <Settings size={12} className="opacity-60 animate-spin" style={{ animationDuration: '4s' }} />
                      Solicitar Cambio de Categoría
                    </button>
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        {!isCropping && (
          <div className="p-4 md:p-8 pt-3 md:pt-4 border-t border-white/5 bg-black/20 flex flex-col gap-3 md:gap-4 shrink-0 mt-auto">
            {error && (
              <p className="text-[9px] md:text-[10px] text-red-500 font-black uppercase text-center animate-bounce">{error}</p>
            )}
            <button
              onClick={handleClose}
              className="w-full bg-white/5 py-3 md:py-4 rounded-xl md:rounded-2xl font-black uppercase text-[10px] md:text-xs hover:bg-white/10 transition-all border border-white/5 text-white shadow-lg shadow-black/20"
            >
              Cerrar y Volver
            </button>
          </div>
        )}
        </motion.div>
      </div>

      <AnimatePresence>
        {requestingCategory && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-slate-900 border border-white/10 p-5 md:p-6 rounded-3xl w-full max-w-md shadow-2xl relative flex flex-col space-y-5"
            >
              <div className="flex justify-between items-center">
                <p className="text-[11px] font-black uppercase tracking-wider text-white">Solicitar Cambio</p>
                <button
                  onClick={() => {
                    setRequestingCategory(false);
                    setRequestedCat('');
                    setAcceptedTerms(false);
                  }}
                  className="text-white/50 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-[9px] font-black uppercase tracking-wider text-slate-300">1. Selecciona Nueva Categoría</label>
                <div className="grid grid-cols-4 gap-2">
                  {categories.filter(c => c !== player.category).map(cat => (
                    <button
                      key={cat}
                      onClick={() => setRequestedCat(cat)}
                      className={`py-3 rounded-xl text-[10px] font-black uppercase transition-all duration-300 border backdrop-blur-md ${
                        requestedCat === cat 
                          ? 'theme-primary-bg text-black shadow-[0_4px_10px_rgba(var(--glow-rgb),0.3)] border-theme-primary' 
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white hover:border-white/20'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {requestedCat && clubs.length > 0 && (
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-wider text-slate-300">2. Selecciona tu Club Validador</label>
                  <div className="relative">
                    <select
                      value={targetClubId}
                      onChange={(e) => setTargetClubId(e.target.value)}
                      className="w-full bg-slate-950/40 border border-white/10 rounded-xl px-3 py-3 outline-none focus:border-theme-primary/40 focus:bg-slate-950/60 font-bold text-xs text-white transition-all duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)] appearance-none"
                    >
                      <option value="" disabled className="bg-slate-900 text-white/50">Elige un organizador...</option>
                      {clubs.map(c => (
                        <option key={c.uid || c.id} value={c.uid || c.id} className="bg-slate-900 text-white">{c.name}</option>
                      ))}
                    </select>
                    <ChevronRight size={14} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-40 theme-primary-text pointer-events-none rotate-90" />
                  </div>
                </div>
              )}

              {requestedCat && targetClubId && (
                <div className="space-y-4 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer group p-3 bg-slate-950/40 rounded-2xl border border-white/5">
                    <div 
                      onClick={() => setAcceptedTerms(!acceptedTerms)}
                      className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center transition-all shrink-0 ${
                        acceptedTerms 
                          ? 'theme-primary-bg border-theme-primary shadow-[0_0_10px_rgba(var(--glow-rgb),0.3)]' 
                          : 'border-white/20 bg-black/40 group-hover:border-white/40'
                      }`}
                    >
                      {acceptedTerms && <Check size={12} className="text-black" strokeWidth={4} />}
                    </div>
                    <span className="text-[10px] font-bold text-white/70 leading-relaxed uppercase select-none group-hover:text-white transition-colors">
                      Considero que mi nivel técnico es compatible para jugar en {requestedCat}.
                    </span>
                  </label>

                  <button
                    onClick={handleRequestCategory}
                    disabled={!requestedCat || !acceptedTerms || !targetClubId || requestLoading}
                    className="w-full theme-primary-bg text-black py-4 rounded-xl font-black uppercase text-xs shadow-xl shadow-theme-primary/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-20 disabled:grayscale btn-sweep-1"
                  >
                    {requestLoading ? (
                      <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                    ) : (
                      'Enviar Solicitud'
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <WhatsAppVerificationModal
        isOpen={showPhonePrompt}
        onClose={() => setShowPhonePrompt(false)}
        userPhone={phone}
        onSuccess={() => {
          setIsPhoneVerified(true);
          setShowPhonePrompt(false);
        }}
      />
    </div>,
    document.body
  );
}
